import performRequest from '@/utils/request';
import { firstSelected, hasMinimumLength } from '@/utils/resultsHelper';
import { endpoints, requestEndpoint } from '@/constants/urls';
import { workspaceReferenceFieldCode } from '@/workspaces/constants';
import { removeUnknownFieldValues, transformForm } from '@/forms/state/state';
import { cohortFromSelectedDictionaries, cohortFromSelectedQueries } from './helper';
import { requestSources } from './constants';
import { sanitizeAttachments } from './attachments/helper';

const requests = (
  filter,
  includePending,
  includeApproved,
  includeDenied,
  includeErrors,
  review,
  page,
  pageSize,
  orderBy,
) => {
  const params = {
    active: true,
    filter,
    review,
    page,
    pageSize,
    orderBy: orderBy || 'updated_at desc',
  };

  if (includePending !== includeApproved
      || includePending !== includeDenied
      || includePending !== includeErrors) {
    if (includePending) {
      params.pending = true;
    }
    if (includeApproved) {
      params.approved = true;
    }
    if (includeDenied) {
      params.denied = true;
    }
    if (includeErrors) {
      params.error = true;
    }
  }

  return performRequest({ url: endpoints.REQUESTS, params, method: 'GET' });
};

const request = (code) => performRequest({ url: requestEndpoint(code), method: 'GET' });

const create = (item, sections) => {
  const { name, code } = item.fields;
  const {
    results, workspaces, terms, attachments, namespaceSelected,
  } = item.steps;
  const fields = removeUnknownFieldValues(item, sections, ['code', 'name', workspaceReferenceFieldCode]);
  const namespace = namespaceSelected ? item.destination_namespace : null;

  const data = {
    name,
    code: code || '',
    fields: transformForm(fields),
    workspace_requested: item.workspace_requested,
    transfer_type: item.transfer.transfer_type,
    terms,
    attachments: sanitizeAttachments(attachments),
  };

  if (hasMinimumLength(namespace)) {
    data.destination_namespace = namespace;
  }

  if (!item.workspace_requested) {
    const workspace = firstSelected(workspaces.results);
    data.workspace_uuid = workspace.uuid;
  } else {
    data.workspace_reference = item.fields[workspaceReferenceFieldCode];
  }

  if (item.request_source === requestSources.DATASET) {
    data.cohort = cohortFromSelectedDictionaries(name, results[0]);
  } else if (item.request_source === requestSources.COHORT) {
    data.user_cohort = cohortFromSelectedQueries(results[0]);
  }

  if (!hasMinimumLength(data.code)) {
    delete data.code;
  }

  return performRequest({ url: endpoints.REQUESTS, data, method: 'POST' });
};

const approve = (code, reason) => {
  const data = { action_reason: reason };
  return performRequest({ url: `${requestEndpoint(code)}/approve`, data, method: 'POST' });
};

const deny = (code, reason) => {
  const data = { action_reason: reason };
  return performRequest({ url: `${requestEndpoint(code)}/deny`, data, method: 'POST' });
};

const transfer = (code, automatic) => {
  const data = { automatic };
  return performRequest({ url: `${requestEndpoint(code)}/transfers`, data, method: 'POST' });
};

const destroy = ({ code }) => performRequest({ url: requestEndpoint(code), method: 'DELETE' });

const archive = ({ code }, reason) => {
  const data = { archived_reason: reason };
  return performRequest({ url: `${requestEndpoint(code)}/archive`, data, method: 'POST' });
};

const workflowStatus = (code) => performRequest({ url: `${requestEndpoint(code)}/workflow/status`, method: 'GET' });

const RequestsService = {
  approve,
  archive,
  create,
  deny,
  destroy,
  request,
  requests,
  transfer,
  workflowStatus,
};

export default RequestsService;
